import { useState, useContext } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { isEmpty, dissoc } from 'ramda'
import { colors } from '@/styles'
import { SearchGrey, LeftArrowIcon } from '@/components/icons'
import { BulkContext } from '@/pages/Chat/context'
import FilterDropdown from './filter-dropdown'
import SupportChatButton from './support-chat-button'
import MobileFilterDrawer from './mobile-filter-drawer'

const ContainerWrapper = styled.div`
  border-bottom: ${props => (props.selectMode ? `1px solid ${colors.grey88}` : '')};
  width: 100%;
`

const Container = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  padding-right: 15px;
`
const SearchContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  padding: 0 10px 0 0;
  height: 100%;
  background-color: ${colors.white};
  border-bottom: 1px solid ${colors.grey};
  border-radius: 6px;
  border: 1.5px solid ${colors.grey88};
  margin-right: 16px;
`

const ButtonWrapper = styled.div`
  cursor: pointer;
  margin-left: 10px;
`

const TextInput = styled.input`
  width: 100%;
  height: 40px;
  padding: 0;
  font-size: 16px;
  border: none;
  background-color: ${colors.white};

  :focus {
    outline: none;
  }

  &::placeholder {
    color: ${colors.mediumGrey};
    opacity: 0.7;
  }
`

const SearchIcon = styled(SearchGrey)`
  flex-shrink: 0;
  cursor: pointer;
  // SearchIcon's padding-left must equal the value of SearchContainer's padding-right
  padding-left: 10px;
  margin-right: 9px;
`

const ClearSearchIcon = styled(LeftArrowIcon)`
  flex-shrink: 0;
  cursor: pointer;
  // ClearSearchIcon's padding-left must equal the value of SearchContainer's padding-right
  padding-left: 10px;
  margin-right: 9px;
  color: ${colors.grey56};
`

const CancelButton = styled.div`
  color: ${colors.link};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
`

const SelectedCounter = styled.div`
  font-size: 16px;
  font-weight: 500;
`

const SideBarSearch = ({
  height,
  placeholder,
  isLandlord,
  history,
  params,
  setParams,
  isMobile,
  currentData
}) => {
  const [searchString, setSearchString] = useState(params?.search ? decodeURI(params?.search) : '')
  const handleSearch = e => setSearchString(e.target.value)
  const parsedParams = dissoc('listing_id', params)
  const parsedParamsOnlyContainsSearch =
    Object.keys(parsedParams).length === 1 && Object.hasOwn(parsedParams, 'search')
  const filterActive = !isEmpty(parsedParams) && !parsedParamsOnlyContainsSearch
  const dispatch = useDispatch()
  const bulkContext = useContext(BulkContext)
  const {
    selectMode,
    setSelectMode,
    selectedChat,
    setSelectedChat,
    isCheckAll,
    setIsCheckAll,
    maxCount
  } = bulkContext

  const handleSearchSubmit = () => {
    if (params?.search && !searchString) {
      dispatch.chat.goingOutOfSearchState()
    }
    setParams('search', searchString ? encodeURI(searchString) : null)
  }

  const handleClearFilters = () => {
    setParams('search', null)
    setSearchString('')
  }

  const selectionLimit = Math.min(currentData.length, maxCount)

  const selectModeHeader = () => {
    return (
      <>
        <CancelButton
          onClick={() => {
            setSelectMode(false)
            setIsCheckAll(false)
          }}
        >
          {t('common.cancel', 'Cancel')}
        </CancelButton>
        <SelectedCounter>{`${selectedChat.length}/${selectionLimit} Selected`}</SelectedCounter>
      </>
    )
  }

  const { t } = useTranslation()
  const filters = isLandlord
    ? [
        {
          id: 'inquiries',
          title: t('l.chat.view.inquiries_tab.title', 'Inquiries')
        },
        {
          id: 'tenants',
          title: t('l.tentants.view.tenants.title', 'Tenants')
        },
        {
          id: 'archived',
          title: t('l.chat.view.archived_tab.title', 'Archived')
        }
      ]
    : [
        {
          id: 'inquiries',
          title: t('l.chat.view.inquiries_tab.title', 'Inquiries')
        }
      ]

  return (
    <ContainerWrapper selectMode={selectMode}>
      <Container>
        {selectMode ? (
          selectModeHeader()
        ) : (
          <SearchContainer isMobile={isMobile} height={height}>
            {searchString ? (
              <ClearSearchIcon
                onClick={() => handleClearFilters()}
                id="clear"
                height={14}
                // Added 10px to the width the allow space for ClearSearchIcon's padding-left and prevent shrinking
                width={24}
              />
            ) : (
              <SearchIcon
                onClick={() => setParams('search', searchString ? encodeURI(searchString) : null)}
                id="search"
                height={18}
                // Added 10px to the width the allow space for SearchIcon's padding-left and prevent shrinking
                width={28}
              />
            )}

            <TextInput
              value={searchString}
              onChange={handleSearch}
              type="text"
              onKeyUp={handleSearchSubmit}
              role="search"
              placeholder={placeholder}
            />
            {isLandlord && (
              <ButtonWrapper>
                {isMobile ? (
                  <MobileFilterDrawer
                    filterActive={filterActive}
                    setParams={setParams}
                    params={params}
                    filters={filters}
                  />
                ) : (
                  <FilterDropdown
                    filters={filters}
                    filterActive={filterActive}
                    setParams={setParams}
                    params={params}
                  />
                )}
              </ButtonWrapper>
            )}
          </SearchContainer>
        )}
        <SupportChatButton
          history={history}
          selectMode={selectMode}
          currentData={currentData}
          selectedChat={selectedChat}
          setSelectedChat={setSelectedChat}
          maxCount={maxCount}
          isCheckAll={isCheckAll}
          setIsCheckAll={setIsCheckAll}
        />
      </Container>
    </ContainerWrapper>
  )
}

export default SideBarSearch
